/* eslint-disable no-param-reassign */
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  IconButton,
  Icon,
  Typography,
  ButtonBase,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@material-ui/core';
import { Link } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import ReactToPrint from 'react-to-print';

import { convertObjToArr, sortArrByKey } from '../../../utils';
import useStyles from './styles';

import ActionsCourse from '../../../store/ducks/course';
import Loading from './Loading';
import MainMenu from '../../../components/MainMenu';

const TopBar = props => {
  const { handleToggleSideMenu, certificate, certificateHtml } = props;
  const dispatch = useDispatch();
  const classes = useStyles();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const {
    list: listCourses,
    error: { list: listCoursesError, current: currentCourseError },
    current,
    loading: { current: requestingCurrent },
  } = useSelector(state => state.course);
  const [courseInfos, setCourseInfos] = useState({});
  const [lessonInfos, setLessonInfos] = useState({});
  const [lessonsModule, setLessonsModule] = useState([]);
  const [paginationText, setPaginationText] = useState('');
  const [loading, setLoading] = useState({
    general: true,
    updateCompleted: false,
  });
  const [error, setError] = useState(false);
  const [updateCompletedError, setUpdateCompletedError] = useState(false);

  const getAllLessonsOrdered = useCallback(() => {
    if (courseInfos.lessons && typeof courseInfos.lessons === 'object') {
      return sortArrByKey(convertObjToArr(courseInfos.lessons), 'order');
    }
    return [];
  }, [courseInfos.lessons]);

  const getPagination = useCallback(() => {
    const lessonsArr = getAllLessonsOrdered();
    const lessonsAmmount = lessonsArr.length;
    const lessonIndex = lessonsArr.findIndex(
      lesson => lesson.id === lessonInfos.id,
    );
    return `${lessonIndex + 1}/${lessonsAmmount}`;
  }, [getAllLessonsOrdered, lessonInfos]);

  useEffect(() => {
    setError(listCoursesError || currentCourseError);
  }, [listCoursesError, currentCourseError]);

  useEffect(() => {
    setLoading(oldLoading => ({ ...oldLoading, general: requestingCurrent }));
  }, [requestingCurrent]);

  useEffect(() => {
    if (
      Object.keys(listCourses).length &&
      current &&
      !listCoursesError &&
      !currentCourseError &&
      current.course in listCourses
    ) {
      setCourseInfos(listCourses[current.course]);
      if (
        current.lesson &&
        !certificate &&
        listCourses[current.course].lessons
      ) {
        setLessonInfos(listCourses[current.course].lessons[current.lesson]);
      }
    }
    return () => {
      setCourseInfos({});
      setLessonInfos({});
    };
  }, [listCourses, listCoursesError, currentCourseError, current, certificate]);

  useEffect(() => {
    if (!courseInfos || !lessonInfos) return;
    if (Object.keys(courseInfos).length && Object.keys(lessonInfos).length) {
      const currentModuleId = lessonInfos.moduleId;
      const lessonsCourseArr = convertObjToArr(courseInfos.lessons);
      const lessonsCurrentModule = lessonsCourseArr.filter(
        lesson => lesson.moduleId === currentModuleId,
      );
      setLessonsModule(sortArrByKey(lessonsCurrentModule, 'order'));
      const pagination = getPagination();
      setPaginationText(pagination);
    }
  }, [courseInfos, getPagination, lessonInfos]);

  useEffect(() => {
    setLoading(oldLoading => ({ ...oldLoading, updateCompleted: false }));
  }, [lessonInfos.isConcluded]);

  useEffect(() => {
    const updateCompletedLessonError = lessonInfos.error?.lessonCompleted;
    setUpdateCompletedError(updateCompletedLessonError);
  }, [lessonInfos.error]);

  useEffect(() => {
    if (updateCompletedError && loading.updateCompleted) {
      setLoading(oldLoading => ({ ...oldLoading, updateCompleted: false }));
      enqueueSnackbar(
        `Não foi possível ${
          lessonInfos.isConcluded ? 'desmarcar' : 'marcar'
        } a aula como concluída`,
        {
          variant: 'error',
        },
      );
    }
  }, [
    enqueueSnackbar,
    updateCompletedError,
    loading.updateCompleted,
    lessonInfos.isConcluded,
  ]);

  const getLessonID = useCallback(
    type => {
      if (!Object.keys(courseInfos).length) return '';
      const lessonsArr = getAllLessonsOrdered();
      let lessonIndex = lessonsArr.findIndex(
        lesson => lesson.id === lessonInfos.id,
      );

      switch (type) {
        case 'prev':
          lessonIndex = lessonIndex === 0 ? lessonIndex : lessonIndex - 1;
          break;
        case 'next':
          lessonIndex =
            lessonIndex === lessonsArr.length - 1
              ? lessonIndex
              : lessonIndex + 1;
          break;
        default:
      }

      return lessonsArr[lessonIndex]?.id;
    },
    [courseInfos, getAllLessonsOrdered, lessonInfos],
  );

  const handleLink = useCallback(
    (location, type) => {
      if (!lessonsModule.length) return '';
      const { pathname } = location;
      let targetPath = pathname.split('/');

      targetPath[targetPath.length - 1] = getLessonID(type);
      targetPath = targetPath.join('/');

      return {
        ...location,
        pathname: targetPath,
      };
    },
    [getLessonID, lessonsModule.length],
  );

  const handleToggleCompleted = useCallback(() => {
    dispatch(
      ActionsCourse.setLessonCompleted(
        current.course,
        current.lesson,
        !lessonInfos?.isConcluded,
      ),
    );
    setUpdateCompletedError(false);
    setLoading(oldLoading => ({ ...oldLoading, updateCompleted: true }));
  }, [current, dispatch, lessonInfos]);

  return (
    <>
      {loading.general && <Loading />}
      {!loading.general && (
        <>
          <Box
            className={`${classes.container} ${
              certificate && 'with-certificate'
            }`}
          >
            <Box className="btn-icon-right-wrapper">
              {isSmallScreen ? (
                <MainMenu />
              ) : (
                <IconButton size="medium" component={Link} to="/">
                  <Icon>arrow_back</Icon>
                </IconButton>
              )}
            </Box>
            {!error && (
              <>
                <Box className="text-title-container">
                  {!isSmallScreen && (
                    <Typography
                      className="text-title-course"
                      variant="caption"
                      noWrap
                    >
                      {courseInfos.name}
                    </Typography>
                  )}
                  <Typography
                    className="text-title-title"
                    variant="subtitle1"
                    noWrap
                    onClick={isSmallScreen ? handleToggleSideMenu : null}
                  >
                    {certificate &&
                      (courseInfos.certificate?.name ||
                        'Certificado de conclusão')}
                    {lessonInfos && lessonInfos.title}
                  </Typography>
                </Box>
                {isSmallScreen && (
                  <IconButton
                    size="medium"
                    className="btn-toggle-sidemenu"
                    onClick={handleToggleSideMenu}
                  >
                    <Icon>menu_open</Icon>
                  </IconButton>
                )}
                {lessonInfos && !certificate && (
                  <>
                    <Box className="pagination">
                      <IconButton
                        className="pagination-back"
                        component={Link}
                        to={location => handleLink(location, 'prev')}
                      >
                        <Icon>chevron_left</Icon>
                      </IconButton>
                      <Typography className="pagination-text" variant="caption">
                        {paginationText}
                      </Typography>
                      <IconButton
                        className="pagination-next"
                        component={Link}
                        to={location => handleLink(location, 'next')}
                      >
                        <Icon>chevron_right</Icon>
                      </IconButton>
                    </Box>
                    <ButtonBase
                      className={`btn-conclusion ${
                        lessonInfos.isConcluded && 'completed'
                      }`}
                      onClick={handleToggleCompleted}
                      disabled={
                        loading.updateCompleted || lessonInfos.daysToRelease > 0
                      }
                    >
                      {loading.updateCompleted ? (
                        <CircularProgress size={theme.spacing(3)} />
                      ) : (
                        <>
                          {lessonInfos.daysToRelease > 0 ? (
                            <>
                              <Typography
                                className="btn-conclusion-text"
                                variant="caption"
                              >
                                Aula bloqueada
                              </Typography>
                              <Icon className="btn-conclusion-icon">lock</Icon>
                            </>
                          ) : (
                            <>
                              <Typography
                                className="btn-conclusion-text"
                                variant="caption"
                              >
                                {lessonInfos.isConcluded
                                  ? 'Concluída'
                                  : 'Marcar como concluída'}
                              </Typography>
                              <Icon className="btn-conclusion-icon">
                                check_circle
                              </Icon>
                            </>
                          )}
                        </>
                      )}
                    </ButtonBase>
                  </>
                )}
                {certificate && (
                  <ReactToPrint
                    trigger={() => (
                      <IconButton className="btn-print">
                        <Icon>print</Icon>
                      </IconButton>
                    )}
                    content={() => certificateHtml.current}
                    print={target => {
                      target.style.top = '0';
                      target.style.left = '0';
                      target.style.width = '0px';
                      target.style.height = '0px';
                      target.style.visibility = 'hidden';
                      target.style.pointerEvents = 'none';
                      target.contentWindow.print();
                      return new Promise(resolve => {
                        resolve();
                      });
                    }}
                  />
                )}
              </>
            )}
          </Box>
        </>
      )}
    </>
  );
};

export default TopBar;
